import { t } from "@lingui/macro";
import { Check, CircleNotch, Eye, FileJs, Upload } from "@phosphor-icons/react";
import { ReactiveResumeParser } from "@reactive-resume/parser";
import type { ResumeData } from "@reactive-resume/schema";
import { buttonVariants, Card, CardContent, CardDescription, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Button, ScrollArea } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef, useState } from "react";
import { ZodError } from "zod";

import { useToast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "../shared/section-icon";

type ValidationResult =
    | {
        isValid: false;
        errors: string;
    }
    | {
        isValid: true;
        result: ResumeData;
    };

export const ImportSection = () => {
    const { toast } = useToast();
    const setValue = useResumeStore((state) => state.setValue);
    const currentResumeData = useResumeStore((state) => state.resume.data);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedFile(file);
        setValidationResult(null);
        validateFile(file);
    };

    const validateFile = async (file: File) => {
        try {
            const parser = new ReactiveResumeParser();
            const data = await parser.readFile(file);
            const result = parser.validate(data);

            setValidationResult({ isValid: true, result });

            toast({
                variant: "success",
                title: "File validated successfully",
                description: "Your resume file is valid and ready to import.",
            });
        } catch (error) {
            if (error instanceof ZodError) {
                console.log(error);
                setValidationResult({
                    isValid: false,
                    errors: error.toString(),
                });

                toast({
                    variant: "error",
                    title: "Invalid file format",
                    description: "The selected file is not a valid Reactive Resume JSON file.",
                });
            }
        }
    };

    const generateChangePreview = () => {
        if (!validationResult?.isValid) return [];

        const parser = new ReactiveResumeParser();
        const importedData = parser.convert(validationResult.result);
        const changes: Array<{
            section: string;
            type: 'added' | 'modified' | 'removed';
            description: string;
            diff?: { before: string; after: string; field?: string };
        }> = [];

        // Helper function to create diff object
        const createDiff = (before: string, after: string, field?: string) => ({
            before: before || '',
            after: after || '',
            field
        });

        // Compare basic information
        if (currentResumeData.basics.name !== importedData.basics.name) {
            changes.push({
                section: 'Basics',
                type: 'modified',
                description: `Name will be changed`,
                diff: createDiff(currentResumeData.basics.name, importedData.basics.name, 'Name')
            });
        }

        if (currentResumeData.basics.email !== importedData.basics.email) {
            changes.push({
                section: 'Basics',
                type: 'modified',
                description: `Email will be changed`,
                diff: createDiff(currentResumeData.basics.email, importedData.basics.email, 'Email')
            });
        }

        if (currentResumeData.basics.phone !== importedData.basics.phone) {
            changes.push({
                section: 'Basics',
                type: 'modified',
                description: `Phone will be changed`,
                diff: createDiff(currentResumeData.basics.phone, importedData.basics.phone, 'Phone')
            });
        }

        if (currentResumeData.basics.headline !== importedData.basics.headline) {
            changes.push({
                section: 'Basics',
                type: 'modified',
                description: `Headline will be changed`,
                diff: createDiff(currentResumeData.basics.headline, importedData.basics.headline, 'Headline')
            });
        }

        if (currentResumeData.basics.location !== importedData.basics.location) {
            changes.push({
                section: 'Basics',
                type: 'modified',
                description: `Location will be changed`,
                diff: createDiff(currentResumeData.basics.location, importedData.basics.location, 'Location')
            });
        }

        // Compare summary
        if (currentResumeData.sections.summary.content !== importedData.sections.summary.content) {
            changes.push({
                section: 'Summary',
                type: 'modified',
                description: 'Summary content will be updated',
                diff: createDiff(currentResumeData.sections.summary.content, importedData.sections.summary.content)
            });
        }

        // Compare sections with detailed diff
        const sectionsToCheck = ['experience', 'education', 'skills', 'projects', 'certifications'] as const;

        sectionsToCheck.forEach(sectionKey => {
            const currentItems = currentResumeData.sections[sectionKey].items;
            const importedItems = importedData.sections[sectionKey].items;
            const sectionName = sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);

            if (currentItems.length !== importedItems.length) {
                changes.push({
                    section: sectionName,
                    type: currentItems.length < importedItems.length ? 'added' : 'removed',
                    description: `Items count: ${currentItems.length} → ${importedItems.length}`,
                    diff: createDiff(
                        `${currentItems.length} items`,
                        `${importedItems.length} items`,
                        'Item Count'
                    )
                });
            }

            // Compare individual items for content changes
            const maxLength = Math.max(currentItems.length, importedItems.length);
            for (let i = 0; i < maxLength; i++) {
                const currentItem = currentItems[i];
                const importedItem = importedItems[i];

                if (!currentItem && importedItem) {
                    // New item added
                    const itemTitle = getItemTitle(importedItem, sectionKey);
                    changes.push({
                        section: sectionName,
                        type: 'added',
                        description: `New item: ${itemTitle}`,
                        diff: createDiff('', itemTitle, `Item ${i + 1}`)
                    });
                } else if (currentItem && !importedItem) {
                    // Item removed
                    const itemTitle = getItemTitle(currentItem, sectionKey);
                    changes.push({
                        section: sectionName,
                        type: 'removed',
                        description: `Removed item: ${itemTitle}`,
                        diff: createDiff(itemTitle, '', `Item ${i + 1}`)
                    });
                } else if (currentItem && importedItem && JSON.stringify(currentItem) !== JSON.stringify(importedItem)) {
                    // Item modified
                    const itemTitle = getItemTitle(currentItem, sectionKey);
                    changes.push({
                        section: sectionName,
                        type: 'modified',
                        description: `Modified item: ${itemTitle}`,
                        diff: createDiff(
                            JSON.stringify(currentItem, null, 2),
                            JSON.stringify(importedItem, null, 2),
                            `Item ${i + 1}: ${itemTitle}`
                        )
                    });
                }
            }
        });

        return changes;
    };

    // Helper function to get item title for display
    const getItemTitle = (item: any, sectionKey: string): string => {
        switch (sectionKey) {
            case 'experience':
                return `${item.position || 'Position'} at ${item.company || 'Company'}`;
            case 'education':
                return `${item.area || 'Study'} at ${item.institution || 'Institution'}`;
            case 'skills':
                return item.name || 'Skill';
            case 'projects':
                return item.name || 'Project';
            case 'certifications':
                return `${item.name || 'Certification'} by ${item.issuer || 'Issuer'}`;
            default:
                return item.name || item.title || 'Item';
        }
    };

    const handlePreviewChanges = () => {
        setShowPreview(true);
    };

    const handleConfirmImport = async () => {
        if (!validationResult?.isValid || !selectedFile) return;

        setLoading(true);
        setShowPreview(false);

        try {
            const parser = new ReactiveResumeParser();
            const data = parser.convert(validationResult.result);

            // Update the current resume data in the store
            // We need to update each top-level property of the resume data
            Object.entries(data).forEach(([key, value]) => {
                setValue(key, value);
            });

            // Reset state after successful import
            setSelectedFile(null);
            setValidationResult(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }

            toast({
                variant: "success",
                title: "Resume imported successfully",
                description: "Your current resume has been updated with the imported data.",
            });
        } catch (error: unknown) {
            toast({
                variant: "error",
                title: "Import failed",
                description: error instanceof Error ? error.message : "An error occurred while importing the resume.",
            });
        } finally {
            setLoading(false);
        }
    };

    const resetImport = () => {
        setSelectedFile(null);
        setValidationResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <section id="import" className="grid gap-y-6 rounded-lg bg-white p-6 shadow-md">
            <header className="flex items-center justify-between">
                <div className="flex items-center gap-x-4">
                    <SectionIcon id="import" size={18} name="Import" />
                    <h2 className="line-clamp-1 text-base font-bold lg:text-lg">Import</h2>
                </div>
            </header>

            <main className="grid gap-y-4">
                <Card
                    className={cn(
                        buttonVariants({ variant: "ghost" }),
                        "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
                        loading && "pointer-events-none cursor-progress opacity-75",
                    )}
                    onClick={handleFileSelect}
                >
                    {loading ? (
                        <CircleNotch size={22} className="animate-spin" />
                    ) : validationResult?.isValid ? (
                        <Check size={22} className="text-green-600" />
                    ) : (
                        <Upload size={22} />
                    )}

                    <CardContent className="flex-1">
                        <CardTitle className="text-sm">
                            {selectedFile ? selectedFile.name : "Select Resume File"}
                        </CardTitle>
                        <CardDescription className="font-normal">
                            {validationResult?.isValid
                                ? "File validated. Click \"Import\" below to update your resume."
                                : "Upload a Reactive Resume JSON file to import your resume data. You can export this format from the Export section above."}
                        </CardDescription>
                    </CardContent>
                </Card>

                {selectedFile && (
                    <div className="flex gap-x-2">
                        {validationResult?.isValid && (
                            <>
                                <Card
                                    className={cn(
                                        buttonVariants({ variant: "outline" }),
                                        "h-auto cursor-pointer flex-row items-center justify-center gap-x-2 px-4 py-2",
                                    )}
                                    onClick={handlePreviewChanges}
                                >
                                    <Eye size={16} />
                                    <span className="text-sm font-medium">Preview Changes</span>
                                </Card>

                                <Card
                                    className={cn(
                                        buttonVariants({ variant: "primary" }),
                                        "h-auto cursor-pointer flex-row items-center justify-center gap-x-2 px-4 py-2",
                                        loading && "pointer-events-none cursor-progress opacity-75",
                                    )}
                                    onClick={handleConfirmImport}
                                >
                                    {loading ? (
                                        <CircleNotch size={16} className="animate-spin" />
                                    ) : (
                                        <FileJs size={16} />
                                    )}
                                    <span className="text-sm font-medium">Import Now</span>
                                </Card>
                            </>
                        )}

                        <Card
                            className={cn(
                                buttonVariants({ variant: "outline" }),
                                "h-auto cursor-pointer flex-row items-center justify-center gap-x-2 px-4 py-2",
                            )}
                            onClick={resetImport}
                        >
                            <span className="text-sm font-medium">Cancel</span>
                        </Card>
                    </div>
                )}

                {validationResult?.isValid === false && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3">
                        <p className="text-sm text-red-700 font-medium">Validation Error</p>
                        <p className="text-xs text-red-600 mt-1">
                            Please ensure you're uploading a valid Reactive Resume JSON file.
                        </p>
                        <pre>
                            {validationResult.errors}
                        </pre>
                    </div>
                )}
            </main>

            <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="hidden"
                aria-label="Select resume file to import"
            />

            {/* Changes Preview Dialog */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Preview Changes</DialogTitle>
                        <DialogDescription>
                            Review the changes that will be made to your resume before confirming the import.
                        </DialogDescription>
                    </DialogHeader>

                    <ScrollArea className="max-h-96">
                        <div className="space-y-3">
                            {(() => {
                                const changes = generateChangePreview();

                                if (changes.length === 0) {
                                    return (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <p>No changes detected. The imported data is identical to your current resume.</p>
                                        </div>
                                    );
                                }

                                return changes.map((change, index) => (
                                    <div key={index} className="rounded-md border overflow-hidden">
                                        {/* Change Header */}
                                        <div className="flex items-center gap-3 p-3 bg-muted/30">
                                            <div className={cn(
                                                "w-2 h-2 rounded-full flex-shrink-0",
                                                change.type === 'added' && "bg-green-500",
                                                change.type === 'modified' && "bg-yellow-500",
                                                change.type === 'removed' && "bg-red-500"
                                            )} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-medium text-sm">{change.section}</span>
                                                    <span className={cn(
                                                        "text-xs px-2 py-0.5 rounded-full",
                                                        change.type === 'added' && "bg-green-100 text-green-700",
                                                        change.type === 'modified' && "bg-yellow-100 text-yellow-700",
                                                        change.type === 'removed' && "bg-red-100 text-red-700"
                                                    )}>
                                                        {change.type === 'added' && "Added"}
                                                        {change.type === 'modified' && "Modified"}
                                                        {change.type === 'removed' && "Removed"}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-muted-foreground break-words">
                                                    {change.description}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Git-style Diff */}
                                        {change.diff && (
                                            <div className="border-t">
                                                {change.diff.field && (
                                                    <div className="px-3 py-1 text-xs font-medium text-muted-foreground bg-muted/20 border-b">
                                                        {change.diff.field}
                                                    </div>
                                                )}
                                                <div className="font-mono text-xs">
                                                    {/* Before (removed/modified) */}
                                                    {change.diff.before && (change.type === 'removed' || change.type === 'modified') && (
                                                        <div className="bg-red-50 border-l-4 border-red-400">
                                                            <div className="px-3 py-2">
                                                                <div className="flex items-start gap-2">
                                                                    <span className="text-red-600 font-bold select-none">-</span>
                                                                    <pre className="whitespace-pre-wrap text-red-700 break-words flex-1">
                                                                        {change.diff.before}
                                                                    </pre>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* After (added/modified) */}
                                                    {change.diff.after && (change.type === 'added' || change.type === 'modified') && (
                                                        <div className="bg-green-50 border-l-4 border-green-400">
                                                            <div className="px-3 py-2">
                                                                <div className="flex items-start gap-2">
                                                                    <span className="text-green-600 font-bold select-none">+</span>
                                                                    <pre className="whitespace-pre-wrap text-green-700 break-words flex-1">
                                                                        {change.diff.after}
                                                                    </pre>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ));
                            })()}
                        </div>
                    </ScrollArea>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPreview(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmImport}
                            disabled={loading}
                            className="gap-2"
                        >
                            {loading ? (
                                <CircleNotch size={16} className="animate-spin" />
                            ) : (
                                <FileJs size={16} />
                            )}
                            Confirm Import
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
};
